/**
 * GlobalSiteSearch - TypeScript ES6 Module Migration
 * Provides navbar search to find sites by reference across all accessible Projects.
 * Migrated from class pattern (global-site-search.js) to typed ES6 class.
 *
 * Phase 12G-K: server-side SP now applies the same visibility filter as
 * GetUserAccessibleSites (RoleLevel >= 6 OR ProjectSiteAssignments / CompanyTeams /
 * Teams membership) — non-admin users without grants get an empty result set,
 * matching the bulk-sites behaviour rather than leaking discovery of all sites.
 *
 * UX notes:
 *   - Result items navigate in the SAME tab (window.location.href, not _blank).
 *   - The results dropdown is appended to document.body and positioned via
 *     getBoundingClientRect() so it escapes the navbar's transform-based
 *     stacking context (.navbar-center-slot uses translateX(-50%) which would
 *     otherwise trap a position: absolute child inside the navbar's box).
 */

import { escapeHtml } from './HtmlUtils.js';

interface SiteSearchResult {
    siteId: number;
    estateId: number;
    siteName: string;
    siteRef: string;
    estateName: string;
    address?: string;
    status?: string;
}

declare global {
    interface Window {
        gtag?: (...args: unknown[]) => void;
        globalSiteSearch?: GlobalSiteSearch;
        GlobalSiteSearch?: typeof GlobalSiteSearch;
    }
}

export class GlobalSiteSearch {
    private container: HTMLElement | null;
    private searchInput!: HTMLInputElement;
    private resultsContainer!: HTMLElement;
    private spinner!: HTMLElement;
    private searchIcon!: HTMLElement;
    private searchTimeout: ReturnType<typeof setTimeout> | null = null;
    private isSearching = false;
    private currentResults: SiteSearchResult[] = [];
    private selectedIndex = -1;
    private repositionListener: (() => void) | null = null;

    constructor(containerId: string) {
        this.container = document.getElementById(containerId);
        if (!this.container) return;
        this.createSearchUI();
        this.bindEvents();
    }

    private createSearchUI(): void {
        if (!this.container) return;
        this.container.innerHTML = `
            <div class="global-search-wrapper">
                <div class="global-search-input-container">
                    <i class="fas fa-magnifying-glass global-search-icon"></i>
                    <input type="text" id="globalSiteSearch" class="global-search-input"
                           placeholder="Search sites by reference..." autocomplete="off" spellcheck="false" />
                    <div class="global-search-spinner" style="display: none;">
                        <i class="fas fa-spinner fa-spin"></i>
                    </div>
                </div>
            </div>
        `;

        this.searchInput = this.container.querySelector('#globalSiteSearch') as HTMLInputElement;
        this.spinner = this.container.querySelector('.global-search-spinner') as HTMLElement;
        this.searchIcon = this.container.querySelector('.global-search-icon') as HTMLElement;

        // Results dropdown is body-attached so it isn't trapped inside the
        // navbar's transform-based stacking context. Position is computed at
        // show-time from the input's getBoundingClientRect().
        this.resultsContainer = document.createElement('div');
        this.resultsContainer.className = 'global-search-results';
        this.resultsContainer.style.display = 'none';
        document.body.appendChild(this.resultsContainer);
    }

    private bindEvents(): void {
        this.searchInput.addEventListener('input', (e: Event) => this.handleInput(e));
        this.searchInput.addEventListener('keydown', (e: KeyboardEvent) => this.handleKeyDown(e));
        this.searchInput.addEventListener('focus', () => this.handleFocus());
        this.searchInput.addEventListener('blur', () => this.handleBlur());
        document.addEventListener('click', (e: MouseEvent) => this.handleClickOutside(e));

        this.searchInput.addEventListener('keypress', (e: KeyboardEvent) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                this.handleEnterKey();
            }
        });

        // Reposition on window resize / scroll so the body-attached dropdown
        // tracks the navbar's input. Listener stays attached for the lifetime
        // of the instance and is removed in destroy().
        this.repositionListener = (): void => {
            if (this.isResultsVisible()) this.positionResults();
        };
        window.addEventListener('resize', this.repositionListener);
        window.addEventListener('scroll', this.repositionListener, true);
    }

    private handleInput(e: Event): void {
        const query: string = (e.target as HTMLInputElement).value.trim();
        if (this.searchTimeout) clearTimeout(this.searchTimeout);
        if (query.length < 2) { this.hideResults(); return; }
        this.searchTimeout = setTimeout(() => { this.performSearch(query); }, 300);
    }

    private handleKeyDown(e: KeyboardEvent): void {
        if (!this.isResultsVisible()) return;
        switch (e.key) {
            case 'ArrowDown': e.preventDefault(); this.navigateResults(1); break;
            case 'ArrowUp': e.preventDefault(); this.navigateResults(-1); break;
            case 'Escape': e.preventDefault(); this.hideResults(); this.searchInput.blur(); break;
            case 'Enter': e.preventDefault(); this.handleEnterKey(); break;
        }
    }

    private handleFocus(): void {
        if (this.currentResults.length > 0 && this.searchInput.value.trim().length >= 2) {
            this.showResults();
        }
    }

    private handleBlur(): void {
        setTimeout(() => {
            // Hide unless focus moved to the dropdown itself (e.g. mouse-down
            // on a result is a "blur then click" sequence).
            const active = document.activeElement;
            const inResults = !!(active && this.resultsContainer.contains(active));
            const inContainer = !!(this.container && this.container.contains(active));
            if (!inResults && !inContainer) {
                this.hideResults();
            }
        }, 150);
    }

    private handleClickOutside(e: MouseEvent): void {
        const target = e.target as Node;
        const inContainer = !!(this.container && this.container.contains(target));
        const inResults = this.resultsContainer.contains(target);
        if (!inContainer && !inResults) {
            this.hideResults();
        }
    }

    private handleEnterKey(): void {
        if (this.selectedIndex >= 0 && this.currentResults[this.selectedIndex]) {
            this.selectResult(this.currentResults[this.selectedIndex]!);
        } else if (this.currentResults.length > 0) {
            this.selectResult(this.currentResults[0]!);
        }
    }

    private async performSearch(query: string): Promise<void> {
        if (this.isSearching) return;
        this.isSearching = true;
        this.showSpinner();

        try {
            const response: Response = await fetch(
                `/api/GlobalSiteSearch?term=${encodeURIComponent(query)}&limit=8`,
                { credentials: 'same-origin' }
            );
            if (!response.ok) throw new Error(`HTTP ${response.status}`);

            this.currentResults = await response.json() as SiteSearchResult[];
            this.selectedIndex = -1;
            this.displayResults();
        } catch (error: unknown) {
            console.error('GlobalSiteSearch: Search error:', error);
            this.showError('Search failed. Please try again.');
        } finally {
            this.isSearching = false;
            this.hideSpinner();
        }
    }

    private displayResults(): void {
        if (this.currentResults.length === 0) {
            this.resultsContainer.innerHTML = `
                <div class="global-search-no-results">
                    <i class="fas fa-magnifying-glass"></i>
                    <span>No sites found matching "${escapeHtml(this.searchInput.value)}"</span>
                </div>`;
        } else {
            this.resultsContainer.innerHTML = this.currentResults.map((site: SiteSearchResult, index: number) => `
                <div class="global-search-result-item" data-index="${index}">
                    <div class="result-main">
                        <div class="result-ref">ID: ${site.siteId}${site.siteRef ? ' · ' + escapeHtml(site.siteRef) : ''}</div>
                        <div class="result-name">${escapeHtml(site.siteName || 'No name')}</div>
                    </div>
                    <div class="result-meta">
                        <div class="result-Project">${escapeHtml(site.estateName || '')}</div>
                        <div class="result-location">${escapeHtml(site.address || '')}</div>
                    </div>
                </div>`).join('');
        }
        this.showResults();
        this.bindResultEvents();
    }

    private bindResultEvents(): void {
        this.resultsContainer.querySelectorAll<HTMLElement>('.global-search-result-item').forEach(
            (item: HTMLElement, index: number) => {
                item.addEventListener('click', () => { this.selectResult(this.currentResults[index]!); });
                item.addEventListener('mouseenter', () => { this.highlightResult(index); });
            }
        );
    }

    private navigateResults(direction: number): void {
        if (this.currentResults.length === 0) return;
        this.selectedIndex += direction;
        if (this.selectedIndex < 0) this.selectedIndex = this.currentResults.length - 1;
        else if (this.selectedIndex >= this.currentResults.length) this.selectedIndex = 0;
        this.highlightResult(this.selectedIndex);
    }

    private highlightResult(index: number): void {
        this.resultsContainer.querySelectorAll<HTMLElement>('.global-search-result-item').forEach(
            (item: HTMLElement) => { item.classList.remove('highlighted'); }
        );
        const selected: HTMLElement | null = this.resultsContainer.querySelector(`[data-index="${index}"]`);
        if (selected) {
            selected.classList.add('highlighted');
            this.selectedIndex = index;
            selected.scrollIntoView({ block: 'nearest' });
        }
    }

    private selectResult(site: SiteSearchResult): void {
        const url: string = this.buildTargetUrl(site);

        if (window.gtag) {
            window.gtag('event', 'global_site_search', {
                'event_category': 'navigation',
                'event_label': site.siteRef
            });
        }

        // Same-tab navigation — full page load is intentional (the destination is a Razor-hosted
        // Blazor page, not an in-app Blazor route we could NavigateTo from vanilla JS).
        this.clear();
        window.location.href = url;
    }

    /**
     * Page-context-aware target (2026-07-23). When the user searches from a Site Details Hub tab
     * (/AIM2/sites/{eid}/{sid}/{view}), land on the SAME tab of the chosen site — Workflow →
     * Workflow, Assets → Assets, and so on. Anywhere else, fall back to the site's default
     * (overview) page via the legacy URL, which 301s to the hub.
     */
    private buildTargetUrl(site: SiteSearchResult): string {
        const view = this.currentSiteDetailsView();
        if (view === null) {
            return `/get/Estate/${site.estateId}/site/${site.siteId}/details`;
        }
        return view
            ? `/AIM2/sites/${site.estateId}/${site.siteId}/${view}`
            : `/AIM2/sites/${site.estateId}/${site.siteId}`;
    }

    /**
     * The current Site Details Hub view segment, or null when we're not on such a page.
     * '' means the bare /AIM2/sites/{eid}/{sid} (overview). The hub validates the segment and
     * defaults unknown ones to overview, so passing the raw tab through is safe + forward-compatible
     * (a new tab needs no change here). pathname excludes query/hash, so the match stays clean.
     */
    private currentSiteDetailsView(): string | null {
        const m = /^\/AIM2\/sites\/\d+\/\d+(?:\/([A-Za-z0-9-]+))?\/?$/.exec(window.location.pathname);
        if (!m) return null;
        return (m[1] ?? '').toLowerCase();
    }

    /** Position the body-attached dropdown beneath the navbar input.
     *  Reads the input wrapper's bounding rect so we follow the navbar's
     *  centred layout exactly, including resizes and theme zoom. */
    private positionResults(): void {
        if (!this.searchInput || !this.resultsContainer) return;
        const wrapper = this.searchInput.closest('.global-search-input-container') as HTMLElement | null;
        const rect = (wrapper ?? this.searchInput).getBoundingClientRect();
        this.resultsContainer.style.top = `${Math.round(rect.bottom + 6)}px`;
        this.resultsContainer.style.left = `${Math.round(rect.left)}px`;
        this.resultsContainer.style.width = `${Math.round(rect.width)}px`;
    }

    private showResults(): void {
        this.positionResults();
        this.resultsContainer.style.display = 'block';
    }
    private hideResults(): void { this.resultsContainer.style.display = 'none'; this.selectedIndex = -1; }
    private isResultsVisible(): boolean { return this.resultsContainer.style.display !== 'none'; }
    private showSpinner(): void { this.searchIcon.style.display = 'none'; this.spinner.style.display = 'block'; }
    private hideSpinner(): void { this.spinner.style.display = 'none'; this.searchIcon.style.display = 'block'; }

    private showError(message: string): void {
        this.resultsContainer.innerHTML = `
            <div class="global-search-error">
                <i class="fas fa-triangle-exclamation"></i>
                <span>${escapeHtml(message)}</span>
            </div>`;
        this.showResults();
    }

    public focus(): void { this.searchInput.focus(); }

    public clear(): void {
        this.searchInput.value = '';
        this.hideResults();
        this.currentResults = [];
        this.selectedIndex = -1;
    }

    public destroy(): void {
        if (this.searchTimeout) clearTimeout(this.searchTimeout);
        if (this.repositionListener) {
            window.removeEventListener('resize', this.repositionListener);
            window.removeEventListener('scroll', this.repositionListener, true);
            this.repositionListener = null;
        }
        if (this.resultsContainer && this.resultsContainer.parentNode) {
            this.resultsContainer.parentNode.removeChild(this.resultsContainer);
        }
        if (this.container) this.container.innerHTML = '';
    }
}

// Expose globally for backward compat
window.GlobalSiteSearch = GlobalSiteSearch;

// Auto-initialize with retry — Blazor Server renders after DOMContentLoaded,
// so the container may not exist yet. Poll until it appears.
document.addEventListener('DOMContentLoaded', () => {
    const CONTAINER_ID = 'globalSiteSearchContainer';
    const MAX_RETRIES = 20;
    const RETRY_MS = 250;
    let attempts = 0;

    function tryInit(): void {
        const container: HTMLElement | null = document.getElementById(CONTAINER_ID);
        if (container) {
            window.globalSiteSearch = new GlobalSiteSearch(CONTAINER_ID);
            return;
        }
        attempts++;
        if (attempts < MAX_RETRIES) {
            setTimeout(tryInit, RETRY_MS);
        }
    }

    tryInit();
});

export default GlobalSiteSearch;
